import { useState } from "react"
import { router } from "@inertiajs/react"
import { Bot, MessageCircle, CheckCircle2 } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, type SelectOption } from "@/components/ui/select"
import { TicketStatusBadge } from "./ticket-status-badge"
import { TicketHistoryPanel } from "./ticket-history-panel"
import { TicketSlaPanel } from "./ticket-sla-panel"
import { EditHistoryPane } from "./edit-history-pane"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { Ticket, TicketPriority } from "@/types/ticket"

type MetaTab = "details" | "timelines" | "actions" | "responses"

const PRIORITY_OPTIONS: SelectOption<TicketPriority>[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high",   label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low",    label: "Low" },
]

const UNASSIGNED_VALUE = "__unassigned__"

type Props = { ticket: Ticket }

// Right rail of the ticket-show 3-col layout. Surfaces requester,
// assignment, tags, status / priority + closure actions. Two close paths:
//   - "Ask Feedback / Resolve" → posts a 👍/👎 prompt and soft-closes the
//     ticket as pending_feedback (auto-resolves in 1h if user doesn't rate)
//   - "Mark resolved" → hard close with no rating prompt
// Reassign is stubbed (no human_assignee column today).
export function TicketMetaPanel({ ticket }: Props) {
  const isClosed         = ticket.status === "closed"
  const isPendingFeedback = ticket.status === "pending_feedback"
  const isInactive       = isClosed || isPendingFeedback
  const [tab, setTab]    = useState<MetaTab>("details")

  function resolve() {
    if (isInactive) return
    if (!confirm("Mark this ticket resolved?")) return
    router.post(`/tickets/${ticket.id}/resolve`)
  }

  function askFeedback() {
    if (isInactive) return
    router.post(`/tickets/${ticket.id}/ask_feedback`)
  }

  const assigneeOptions: SelectOption<string>[] = [
    { value: UNASSIGNED_VALUE, label: "Unassigned" },
    ...(ticket.eligibleAssignees ?? []).map((u) => ({
      value: String(u.id),
      label: u.name ? `${u.name} (${u.email})` : u.email,
    })),
  ]
  const currentAssigneeValue = ticket.humanAssignee
    ? String(ticket.humanAssignee.id)
    : UNASSIGNED_VALUE
  const currentAssigneeOption =
    assigneeOptions.find((o) => o.value === currentAssigneeValue) ?? assigneeOptions[0]

  function reassign(value: string) {
    const userId = value === UNASSIGNED_VALUE ? "" : value
    router.post(
      `/tickets/${ticket.id}/reassign`,
      { user_id: userId },
      { preserveScroll: true },
    )
  }

  const currentPriorityOption =
    PRIORITY_OPTIONS.find((o) => o.value === ticket.priority) ?? PRIORITY_OPTIONS[2]

  function changePriority(value: TicketPriority) {
    if (value === ticket.priority) return
    router.patch(
      `/tickets/${ticket.id}`,
      { priority: value },
      { preserveScroll: true },
    )
  }

  const requesterInitial =
    (ticket.requester.name || ticket.requester.email || ticket.requester.sourceKey || "?")
      .trim()[0]
      ?.toUpperCase() ?? "?"
  const stateActive = ticket.requester.state === "active"

  return (
    <aside className="flex w-1/2 flex-col gap-4 overflow-y-auto border-l border-border bg-app-panel p-4">
      {/* Top: Requester card — identity at a glance + closure actions.
          Layout matches the redesigned ticket-detail spec: avatar + name +
          email on top, source_key + state pill side-by-side, separator,
          assigned AI row, then the two close-paths as full-width buttons. */}
      <Card variant="inset" className="space-y-4 p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-brand-red/40 bg-brand-redSoft text-lg font-semibold text-brand-red">
            {requesterInitial}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-app-subtle">Requester</div>
              <div className="truncate text-base font-semibold text-app-text">
                {ticket.requester.name || ticket.requester.sourceKey}
              </div>
              {ticket.requester.email && (
                <div className="truncate text-sm text-app-muted">{ticket.requester.email}</div>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
              <div className="min-w-0 flex-1">
                <div className="mb-1 text-[11px] uppercase tracking-wide text-app-subtle">Source</div>
                <code className="block truncate rounded-control border border-border bg-black/30 px-2 py-1 font-mono text-[11px] text-app-text">
                  {ticket.requester.sourceKey}
                </code>
              </div>
              <div>
                <div className="mb-1 text-[11px] uppercase tracking-wide text-app-subtle">State</div>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-control border px-2 py-1 text-xs",
                    stateActive
                      ? "border-status-success/40 bg-status-success/10 text-status-success"
                      : "border-status-error/40 bg-status-error/10 text-status-error",
                  )}
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      stateActive ? "bg-status-success" : "bg-status-error",
                    )}
                  />
                  {ticket.requester.state}
                </span>
              </div>
            </div>
            {ticket.requester.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {ticket.requester.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-chip border border-border bg-app-panel2 px-2 py-0.5 text-[11px] text-app-text"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <hr className="border-border/60" />

        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-brand-red/40 bg-brand-redSoft text-brand-red">
            <Bot size={22} strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-app-subtle">Assigned AI</div>
            <div className="text-base font-semibold text-app-text">{ticket.assignedAgent.name}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="gold" onClick={askFeedback} disabled={isInactive}>
            <MessageCircle size={14} strokeWidth={2} className="mr-1.5" />
            {isPendingFeedback ? "Awaiting feedback" : "Ask for feedback & resolve"}
          </Button>
          <Button onClick={resolve} disabled={isInactive}>
            <CheckCircle2 size={14} strokeWidth={2} className="mr-1.5" />
            {isClosed ? "Already closed" : isPendingFeedback ? "Soft-closed" : "Mark resolved"}
          </Button>
        </div>
      </Card>

      {/* Below the Requester card: tab strip spanning the full width.
          - Details: current ticket state (status / priority / assignee /
            tags / escalation reason).
          - Timelines: this ticket's change log (audit-driven) + this
            requester's other tickets.
          - Actions: placeholder for things we want to do with the user.
          - Responses: placeholder for canned-response shortcuts. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <div className="flex gap-1 border-b border-border">
          <TabButton active={tab === "details"}   onClick={() => setTab("details")}>Details</TabButton>
          <TabButton active={tab === "timelines"} onClick={() => setTab("timelines")}>Timelines</TabButton>
          <TabButton active={tab === "actions"}   onClick={() => setTab("actions")}>Actions</TabButton>
          <TabButton active={tab === "responses"} onClick={() => setTab("responses")}>Responses</TabButton>
        </div>

        {tab === "details" && (
          <div className="flex flex-col gap-4">
            <Card variant="inset" className="space-y-3 p-4">
              <Section label="Status">
                <TicketStatusBadge status={ticket.status} />
              </Section>
              <Section label="Priority">
                <div className="space-y-1">
                  <Select<TicketPriority>
                    value={currentPriorityOption}
                    options={PRIORITY_OPTIONS}
                    isDisabled={isInactive}
                    onChange={(o) => o && changePriority(o.value)}
                  />
                  {ticket.triageAppliedAt && (
                    <div className="text-[11px] text-app-subtle">
                      Auto-triaged {fmtCompactRelative(ticket.triageAppliedAt)}
                    </div>
                  )}
                </div>
              </Section>
              <Section label="Assignee">
                <Select<string>
                  value={currentAssigneeOption}
                  options={assigneeOptions}
                  isDisabled={isInactive}
                  onChange={(o) => o && reassign(o.value)}
                />
              </Section>
            </Card>

            <TicketSlaPanel sla={ticket.sla} />

            {ticket.tags.length > 0 && (
              <Card variant="inset" className="space-y-3 p-4">
                <Section label="Tags">
                  <div className="flex flex-wrap gap-1.5">
                    {ticket.tags.map((t) => (
                      <span key={t} className="rounded-chip bg-white/5 px-2 py-0.5 text-xs text-app-muted">
                        {t}
                      </span>
                    ))}
                  </div>
                </Section>
              </Card>
            )}

            {(ticket.escalationReason || ticket.escalationReasonText) && (
              <Card variant="inset" className="space-y-3 p-4">
                <Section label="Escalation reason">
                  {ticket.escalationReason && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-control border border-status-error/30 bg-status-error/10 px-2 py-0.5 text-[11px] uppercase tracking-wide text-status-error ${ticket.escalationReason.status === "pending" ? "opacity-60" : ""}`}
                      title={ticket.escalationReason.description}
                    >
                      {ticket.escalationReason.name}
                      {ticket.escalationReason.status === "pending" && " (pending review)"}
                    </span>
                  )}
                  {ticket.escalationReasonText && (
                    <p className="mt-1 text-sm text-app-muted whitespace-pre-wrap">{ticket.escalationReasonText}</p>
                  )}
                </Section>
              </Card>
            )}
          </div>
        )}

        {tab === "timelines" && (
          <div className="flex min-h-0 flex-1 gap-4">
            <aside className="flex w-1/2 flex-col gap-3 overflow-y-auto rounded-card border border-border bg-app-panel2/40 p-4">
              <header className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-app-text">Change log</h2>
                <span className="text-[11px] text-app-muted">
                  {ticket.changeLog?.length ?? 0}
                </span>
              </header>
              <EditHistoryPane entries={ticket.changeLog ?? []} compact />
            </aside>
            <div className="w-1/2">
              <TicketHistoryPanel rows={ticket.appuserHistory ?? []} />
            </div>
          </div>
        )}

        {tab === "actions" && (
          <Card variant="inset" className="p-6 text-center text-sm text-app-subtle">
            No actions yet.
          </Card>
        )}

        {tab === "responses" && (
          <Card variant="inset" className="p-6 text-center text-sm text-app-subtle">
            Responses — coming soon.
          </Card>
        )}
      </div>
    </aside>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-app-subtle">{label}</div>
      {children}
    </div>
  )
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-3 py-2 text-xs font-medium uppercase tracking-wide transition",
        active
          ? "border-brand-red text-app-text"
          : "border-transparent text-app-muted hover:text-app-text",
      )}
    >
      {children}
    </button>
  )
}
